import { Json } from 'ox'
import type posthogJs from 'posthog-js'

const api_host = '/h0g-tunnel'
const appId = 'wallet'
const key = import.meta.env.VITE_POSTHOG_KEY

let posthog: typeof posthogJs | undefined
let initialized = false
let initPromise: Promise<void> | undefined

/** Initializes PostHog analytics when configured. */
export function init(): Promise<void> {
  if (initPromise) return initPromise
  if (import.meta.env.DEV || typeof window === 'undefined' || !key) return Promise.resolve()

  initPromise = (async () => {
    const m = await import('posthog-js')
    posthog = m.default
    const options: Parameters<typeof posthog.init>[1] & {
      prepare_external_dependency_script(script: HTMLScriptElement): HTMLScriptElement
    } = {
      api_host,
      ui_host: 'https://us.posthog.com',
      autocapture: true,
      capture_pageleave: true,
      capture_pageview: true,
      person_profiles: 'identified_only',
      loaded(instance) {
        // PostHog only exposes host-level proxy settings, so patch the router to
        // avoid browser-visible paths that Brave/EasyPrivacy blocks.
        const router = (
          instance as never as {
            requestRouter: {
              endpointFor(target: 'api' | 'assets' | 'flags' | 'ui', path?: string): string
            }
          }
        ).requestRouter
        const endpoint_for = router.endpointFor.bind(router)
        router.endpointFor = (target, path) => {
          const endpoint = endpoint_for(target, path)
          const url = new URL(endpoint, window.location.origin)
          const path_current = url.pathname.slice(api_host.length)
          if (path_current === '/e' || path_current.startsWith('/e/'))
            url.pathname = `${api_host}/a${path_current.slice('/e'.length)}`
          if (path_current === '/flags' || path_current.startsWith('/flags/'))
            url.pathname = `${api_host}/b${path_current.slice('/flags'.length)}`
          if (path_current === '/i/v0/e' || path_current.startsWith('/i/v0/e/'))
            url.pathname = `${api_host}/c${path_current.slice('/i/v0/e'.length)}`
          if (path_current === '/s' || path_current.startsWith('/s/'))
            url.pathname = `${api_host}/q${path_current.slice('/s'.length)}`
          if (path_current === '/static/dead-clicks-autocapture.js')
            url.pathname = `${api_host}/static/d.js`
          if (path_current === '/static/posthog-recorder.js')
            url.pathname = `${api_host}/static/r.js`
          return endpoint.startsWith(api_host)
            ? `${url.pathname}${url.search}${url.hash}`
            : url.toString()
        }
      },
      prepare_external_dependency_script(script) {
        const url = new URL(script.src)
        const path = url.pathname.slice(api_host.length)
        if (path === '/static/dead-clicks-autocapture.js') url.pathname = `${api_host}/static/d.js`
        if (path === '/static/posthog-recorder.js') url.pathname = `${api_host}/static/r.js`
        script.src = url.toString()
        return script
      },
    }
    // Guard against posthog-js itself being initialized elsewhere (e.g. rrweb plugin).
    if (!posthog.__loaded) posthog.init(key, options)
    posthog.register({ tempo_app_id: appId })
    initialized = true
  })().catch((error) => {
    // Reset so a later call can retry instead of being stuck on a rejected promise.
    initPromise = undefined
    posthog = undefined
    initialized = false
    throw error
  })
  return initPromise
}

/** Identifies the current wallet or account in PostHog. */
export async function identify(address: string) {
  await init()
  if (!initialized) return
  posthog!.identify(address.toLowerCase(), { address: address.toLowerCase() })
}

/** Identifies an authenticated wallet and captures an active-session event. */
export async function active(address: string) {
  await init()
  if (!initialized) return
  const address_normalized = address.toLowerCase()
  posthog!.identify(address_normalized, { address: address_normalized })
  posthog!.capture('wallet_active')
}

/** Captures a PostHog event when analytics are enabled. */
export async function capture(
  event: string,
  properties: Record<string, unknown> = {},
  defaults: Record<string, unknown> = {},
) {
  await init()
  if (!initialized) return
  posthog!.capture(event, sanitizeProperties({ ...defaults, ...properties }))
}

/** Clears the current PostHog identity. */
export function reset() {
  if (!initialized) return
  posthog!.reset()
}

export function sanitizeProperties(properties: Record<string, unknown>): Record<string, unknown> {
  return JSON.parse(Json.stringify(properties)) as Record<string, unknown>
}
